import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from './context/ThemeContext';
import { useAuth } from './hooks/useAuth';
import ScrollToTop from './components/ScrollToTop';
import { initGlobalPresence } from './lib/onlineStatusApi';

// --- Static core shell (landing, auth) ---
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import CookieBanner from './components/CookieBanner';

// --- Lazy loaded public pages ---
const BlogListPage = lazy(() => import('./pages/BlogListPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const JoinAcademyPage = lazy(() => import('./pages/JoinAcademyPage'));
const AboutUsPage = lazy(() => import('./pages/AboutUsPage'));
const AcademyPage = lazy(() => import('./pages/AcademyPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const HistoryTimelinePage = lazy(() => import('./pages/HistoryTimelinePage'));
const PublicCourseLandingPage = lazy(() => import('./pages/PublicCourseLandingPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const PublicPage = lazy(() => import('./pages/PublicPage'));

// --- Lazy loaded layout ---
const MasterLayout = lazy(() => import('./components/layout/MasterLayout'));
const DashboardPage = lazy(() => import('./pages/platform/DashboardPage'));
const CoursesPage = lazy(() => import('./pages/platform/CoursesPage'));
const SingleCoursePage = lazy(() => import('./pages/platform/SingleCoursePage'));
const CareerPathViewPage = lazy(() => import('./pages/platform/CareerPathViewPage'));
const AffiliatePage = lazy(() => import('./pages/platform/AffiliatePage'));
const UserProfilePage = lazy(() => import('./pages/community/UserProfilePage'));

// --- Lazy loaded Community (Circle.so-inspired) ---
const CommunityFeedPage = lazy(() => import('./pages/community/CommunityFeedPage'));
const ChannelPostsPage = lazy(() => import('./pages/community/ChannelPostsPage'));
const ChannelChatPage = lazy(() => import('./pages/community/ChannelChatPage'));
const PostDetailPage = lazy(() => import('./pages/community/PostDetailPage'));
const CommunityMembersPage = lazy(() => import('./pages/community/MembersPage'));
const LeaderboardPage = lazy(() => import('./pages/community/LeaderboardPage'));
const SavedItemsPage = lazy(() => import('./pages/community/SavedItemsPage'));
const EventsPage = lazy(() => import('./pages/community/EventsPage'));
const HashtagFeedPage = lazy(() => import('./pages/community/HashtagFeedPage'));

// --- Lazy loaded Admin CMS ---
const AdminLayout = lazy(() => import('./components/admin/AdminLayout'));
const AdminDashboardPage = lazy(() => import('./pages/admin/AdminDashboardPage'));
const AdminCoursesPage = lazy(() => import('./pages/admin/AdminCoursesPage'));
const AdminCareerPathsPage = lazy(() => import('./pages/admin/AdminCareerPathsPage'));
const AdminCareerPathEditorPage = lazy(() => import('./pages/admin/AdminCareerPathEditorPage'));
const AdminCourseBuilderPage = lazy(() => import('./pages/admin/courses/AdminCourseBuilderPage'));
const AdminPostsPage = lazy(() => import('./pages/admin/AdminPostsPage'));
const AdminPostEditorPage = lazy(() => import('./pages/admin/AdminPostEditorPage'));
const AdminCategoriesPage = lazy(() => import('./pages/admin/AdminCategoriesPage'));
const AdminTagsPage = lazy(() => import('./pages/admin/AdminTagsPage'));
const AdminMediaPage = lazy(() => import('./pages/admin/AdminMediaPage'));
const AdminSettingsPage = lazy(() => import('./pages/admin/AdminSettingsPage'));
const AdminPagesPage = lazy(() => import('./pages/admin/AdminPagesPage'));
const AdminSystemSeoPage = lazy(() => import('./pages/admin/AdminSystemSeoPage'));
const AdminPageEditorPage = lazy(() => import('./pages/admin/AdminPageEditorPage'));
const AdminFooterMenuPage = lazy(() => import('./pages/admin/AdminFooterMenuPage'));
const AdminAffiliatePage = lazy(() => import('./pages/admin/AdminAffiliatePage'));
const AdminUsersPage = lazy(() => import('./pages/admin/AdminUsersPage'));
const AdminRegisterPage = lazy(() => import('./pages/admin/AdminRegisterPage'));
const AdminCommunityPage = lazy(() => import('./pages/admin/AdminCommunityPage'));
const AdminClientsPage = lazy(() => import('./pages/admin/AdminClientsPage'));
const AdminHistoryTimelinePage = lazy(() => import('./pages/admin/AdminHistoryTimelinePage'));

const DMLayout = lazy(() => import('./components/community/dm/DMLayout'));

const PageLoader = () => (
    <div className="h-screen w-full flex items-center justify-center bg-[#0a0a0a]">
        <div className="w-12 h-12 border-4 border-[#C1B392]/20 border-t-[#C1B392] rounded-full animate-spin"></div>
    </div>
);

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
    const { user, loading } = useAuth();

    if (loading) {
        return <div className="h-screen flex items-center justify-center">Ładowanie...</div>;
    }

    if (!user) {
        return <Navigate to="/zaloguj" replace />;
    }

    return <>{children}</>;
};

const AdminProtectedRoute = ({ children }: { children: React.ReactNode }) => {
    const { user, role, loading } = useAuth();

    if (loading) {
        return <div className="h-screen flex items-center justify-center">Sprawdzam uprawnienia...</div>;
    }

    if (!user || (role !== 'admin' && role !== 'superadmin' && !role?.startsWith('role_'))) {
        const urlParams = new URLSearchParams(window.location.search);
        // if missing rights, kick back to platform dashboard
        return <Navigate to="/platforma/dashboard" replace />;
    }

    return <>{children}</>;
};

export default function App() {
    const { initialize, user, profile } = useAuth();

    useEffect(() => {
        initialize();

        // Load global settings for document title and favicon
        import('./lib/cms').then(({ settingsApi }) => {
            settingsApi.get().then(settings => {
                if (settings.site_name) {
                    document.title = settings.site_name;
                }
                if (settings.favicon_url) {
                    let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
                    if (!link) {
                        link = document.createElement('link');
                        link.rel = 'icon';
                        document.head.appendChild(link);
                    }
                    link.href = settings.favicon_url;
                }
                if (settings.seo_noindex !== 'false') {
                    let meta = document.querySelector('meta[name="robots"]') as HTMLMetaElement;
                    if (!meta) {
                        meta = document.createElement('meta');
                        meta.name = 'robots';
                        document.head.appendChild(meta);
                    }
                    meta.content = 'noindex, nofollow';
                } else {
                    let meta = document.querySelector('meta[name="robots"]') as HTMLMetaElement;
                    if (meta) {
                        meta.remove();
                    }
                }
            }).catch(console.error);
        });
    }, [initialize]);

    useEffect(() => {
        if (user && profile) {
            const fullName = `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'Użytkownik';
            initGlobalPresence(user.id, fullName, profile.avatar_url);
        }
    }, [user, profile]);

    // Standard mode with router
    return (
        <ThemeProvider>
            <BrowserRouter>
                <ScrollToTop />
                <Suspense fallback={<PageLoader />}>
                    <Routes>
                        <Route path="/" element={<LandingPage />} />
                        <Route path="/zaloguj" element={<LoginPage />} />
                        <Route path="/rejestracja" element={<RegisterPage />} />

                        {/* Public but lazy */}
                        <Route path="/dolacz" element={<JoinAcademyPage />} />
                        <Route path="/blog" element={<BlogListPage />} />
                        <Route path="/blog/:slug" element={<BlogPostPage />} />
                        <Route path="/o-nas" element={<AboutUsPage />} />
                        <Route path="/o-akademii" element={<AcademyPage />} />

                        <Route path="/kontakt" element={<ContactPage />} />
                        <Route path="/nasza-historia" element={<HistoryTimelinePage />} />
                        <Route path="/aktualnosci" element={<BlogListPage />} />
                        <Route path="/aktualnosci/:slug" element={<BlogPostPage />} />
                        <Route path="/strona/:slug" element={<PublicPage />} />
                        <Route path="/checkout/:id" element={<CheckoutPage />} />
                        <Route path="/:idOrSlug" element={<PublicCourseLandingPage />} />

                        <Route path="/admin/setup/:token" element={<AdminRegisterPage />} />

                        {/* NEW Unified Master Layout */}
                        <Route path="/platforma" element={
                            <ProtectedRoute>
                                <MasterLayout />
                            </ProtectedRoute>
                        }>
                            <Route index element={<Navigate to="/platforma/spolecznosc" replace />} />
                            <Route path="dashboard" element={<DashboardPage />} />
                            
                            {/* Community Section */}
                            <Route path="spolecznosc" element={<CommunityFeedPage />} />
                            <Route path="spolecznosc/s/:serverSlug" element={<CommunityFeedPage />} />
                            <Route path="spolecznosc/s/:serverSlug/kanal/:channelSlug" element={<ChannelPostsPage />} />
                            <Route path="spolecznosc/s/:serverSlug/kanal/:channelSlug/czat" element={<ChannelChatPage />} />
                            <Route path="spolecznosc/s/:serverSlug/wydarzenia" element={<EventsPage />} />
                            <Route path="spolecznosc/post/:postId" element={<PostDetailPage />} />
                            <Route path="spolecznosc/zapisane" element={<SavedItemsPage />} />
                            <Route path="spolecznosc/czlonkowie" element={<CommunityMembersPage />} />
                            <Route path="spolecznosc/tablica" element={<LeaderboardPage />} />
                            <Route path="spolecznosc/tag/:hashtag" element={<HashtagFeedPage />} />
                            <Route path="spolecznosc/profil" element={<UserProfilePage />} />
                            <Route path="spolecznosc/profil/:id" element={<UserProfilePage />} />
                            <Route path="spolecznosc/partnerzy" element={<AffiliatePage />} />
                            
                            {/* Academy Section */}
                            <Route path="szkolenia" element={<CoursesPage />} />
                            <Route path="szkolenia/:id" element={<SingleCoursePage />} />
                            <Route path="sciezki/:slug" element={<CareerPathViewPage />} />

                            {/* DMs routing */}
                            <Route path="spolecznosc/wiadomosci/*" element={<DMLayout />} />
                        </Route>

                        {/* ADMIN CMS */}
                        <Route path="/admin" element={
                            <AdminProtectedRoute>
                                <AdminLayout />
                            </AdminProtectedRoute>
                        }>
                            <Route index element={<Navigate to="/admin/dashboard" replace />} />
                            <Route path="dashboard" element={<AdminDashboardPage />} />
                            <Route path="szkolenia" element={<AdminCoursesPage />} />
                            <Route path="sciezki" element={<AdminCareerPathsPage />} />
                            <Route path="sciezki/:id" element={<AdminCareerPathEditorPage />} />
                            <Route path="szkolenia/:id/buduj" element={<AdminCourseBuilderPage />} />
                            <Route path="spolecznosc" element={<AdminCommunityPage />} />
                            <Route path="klienci" element={<AdminClientsPage />} />

                            {/* CMS Routes */}
                            <Route path="wpisy" element={<AdminPostsPage />} />
                            <Route path="wpisy/:id" element={<AdminPostEditorPage />} />
                            <Route path="strony" element={<AdminPagesPage />} />
                            <Route path="seo-systemowe" element={<AdminSystemSeoPage />} />
                            <Route path="strony/:id" element={<AdminPageEditorPage />} />
                            <Route path="menu-stopki" element={<AdminFooterMenuPage />} />
                            <Route path="kategorie" element={<AdminCategoriesPage />} />
                            <Route path="tagi" element={<AdminTagsPage />} />
                            <Route path="media" element={<AdminMediaPage />} />
                            <Route path="ustawienia" element={<AdminSettingsPage />} />
                            <Route path="afiliacja" element={<AdminAffiliatePage />} />
                            <Route path="uzytkownicy" element={<AdminUsersPage />} />
                            <Route path="nasza-historia" element={<AdminHistoryTimelinePage />} />
                        </Route>
                    </Routes>
                </Suspense>
                <CookieBanner />
            </BrowserRouter>
        </ThemeProvider>
    );
}
